import { useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { ThemeToggle } from '../components/ThemeToggle.jsx';
import { ApiError, api } from '../lib/api.js';
import './LoginPage.css';

/**
 * Click sul logo necessari a mostrare l'accesso Microsoft.
 *
 * L'SSO è per il personale interno; i clienti entrano con le credenziali
 * AtlantePlus. Tenerlo nascosto evita che un cliente ci provi, si veda rifiutare
 * dal tenant e chiami l'assistenza — ma non è una misura di sicurezza: il
 * bottone si limita a portare su un endpoint che decide da sé chi ammettere.
 */
const SSO_CLICKS = 3;

/**
 * Il tratto ondulato che separa il form dall'illustrazione.
 *
 * Non è una linea con un contorno, è una figura piena: una linea disegnata con
 * `stroke` ha spessore costante e non si può assottigliare alle estremità. Qui
 * lo spessore è una funzione dell'altezza — zero alle due punte, massimo a metà
 * — e il tracciato che ne esce è il contorno del nastro, percorso in giù sul
 * lato destro e in su su quello sinistro.
 *
 * Coordinate 0..1 su tutta la LARGHEZZA DELLA PAGINA: `ONDA_X = 0.53` si legge
 * come «al 53% della pagina», appena oltre la metà.
 *
 * Conseguenza di `preserveAspectRatio="none"`, che stira il disegno sulla
 * finestra: lo spessore è una frazione della larghezza, quindi cresce con essa —
 * ~14px su un monitor da 1280, ~18px su uno da 1600. Con `stroke` si sarebbe
 * potuto congelare (`vector-effect`), con una figura piena no. È il prezzo delle
 * punte, e su schermi normali si nota appena.
 */
const ONDA_X = 0.53;

/** Dove il nastro comincia e finisce: dentro la pagina, non sui bordi. */
const ONDA_DA = 0.05;
const ONDA_A = 0.95;

/** Spessore nel punto più largo, in frazioni di larghezza pagina. */
const ONDA_SPESSORE = 0.011;

/**
 * IN PROVA: varianti ammesse per `?onda=`. Sono i parametri della sinusoide,
 * non più tracciati scritti a mano: le gobbe e quanto sporgono.
 *
 * Meno gobbe ci sono, più i raccordi sono lunghi e più la curva risulta
 * morbida — per questo il default è a due.
 */
const ONDE: Record<string, { gobbe: number; ampiezza: number } | null> = {
  /* Una sola S lunga su tutta l'altezza: il default. */
  '2-035': { gobbe: 2, ampiezza: 0.035 },
  /* Stessa S, più aperta. */
  '2-05': { gobbe: 2, ampiezza: 0.05 },
  '3-03': { gobbe: 3, ampiezza: 0.03 },
  /* La più mossa. */
  '4-025': { gobbe: 4, ampiezza: 0.025 },
  /* Nessun tratto: le due metà restano affiancate senza segno di confine. */
  no: null,
};

/**
 * Il contorno del nastro, campionato punto per punto.
 *
 * La linea di mezzo è una sinusoide vera — `x = centro + ampiezza · sin(π·n·y)`
 * — e non l'approssimazione con cubiche che serviva quando il tracciato era
 * scritto a mano: i flessi sono esatti, non raccordati a occhio.
 *
 * Il profilo dello spessore è `sin(π·t)` elevato a 0,6: l'esponente sotto 1
 * tiene il nastro pieno per gran parte della corsa e concentra
 * l'assottigliamento vicino alle punte. Con l'esponente a 1 sembrerebbe una
 * foglia, con 0,3 una barra che si spezza di colpo.
 *
 * 96 campioni: la linea è quasi verticale, le corde fra un punto e l'altro sono
 * lunghe una decina di pixel e non si distingue dalla curva.
 */
function nastroOnda(gobbe: number, ampiezza: number, spessore: number): string {
  const PASSI = 96;
  const destra: string[] = [];
  const sinistra: string[] = [];

  for (let i = 0; i <= PASSI; i += 1) {
    const t = i / PASSI;
    const y = ONDA_DA + t * (ONDA_A - ONDA_DA);
    const x = ONDA_X + ampiezza * Math.sin(Math.PI * gobbe * y);
    const mezzo = (spessore / 2) * Math.sin(Math.PI * t) ** 0.6;
    destra.push(`${(x + mezzo).toFixed(4)},${y.toFixed(4)}`);
    sinistra.push(`${(x - mezzo).toFixed(4)},${y.toFixed(4)}`);
  }

  return `M${destra.join(' L')} L${sinistra.reverse().join(' L')} Z`;
}

/**
 * Il bordo del fondo scuro (tema scuro, vedi LoginPage.css).
 *
 * Stessa sinusoide della linea di mezzo del nastro, ma senza spessore e
 * prolungata fino ai bordi: il nastro finisce in punta a 0,05 e 0,95, il fondo
 * no — deve arrivare al bordo della pagina, altrimenti sopra e sotto resta uno
 * scalino bianco. Chiudendo su x=0 si ottiene la regione a sinistra della
 * curva, che e' esattamente la meta' da scurire.
 *
 * `ampiezza: 0` (variante `?onda=no`) degenera in una retta a ONDA_X: il fondo
 * resta netto come la pagina, che in quel caso non ha nessun segno di confine.
 */
function fondoOnda(gobbe: number, ampiezza: number): string {
  const PASSI = 96;
  const punti: string[] = [];

  for (let i = 0; i <= PASSI; i += 1) {
    const y = i / PASSI;
    const x = ONDA_X + ampiezza * Math.sin(Math.PI * gobbe * y);
    punti.push(`${x.toFixed(4)},${y.toFixed(4)}`);
  }

  return `M0,0 L${punti.join(' L')} L0,1 Z`;
}

/**
 * Errori che il callback SSO rimanda in query string. Tradotti qui: la SPA è
 * l'unico posto che parla all'utente, e il server non deve conoscere le frasi.
 */
const SSO_ERRORS: Record<string, string> = {
  annullato: 'Accesso Microsoft annullato.',
  sessione_scaduta: 'La richiesta di accesso è scaduta. Riprova.',
  non_valido: 'Richiesta di accesso non valida. Riprova.',
  usa_atlante: 'Questa utenza accede con le credenziali Atlante, non con Microsoft.',
  non_abilitato: 'Utenza non abilitata al portale. Contatta l\'assistenza.',
  errore: 'Accesso Microsoft non riuscito. Riprova fra poco.',
};

export function LoginPage({ onSuccess }: { onSuccess: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [logoClicks, setLogoClicks] = useState(0);

  /**
   * L'esito dell'invio, mostrato sul pulsante prima che la pagina cambi.
   *
   * `attesa` — senza, l'accesso riuscito è indistinguibile da un clic a vuoto
   * finché la schermata dopo non è pronta: si vede solo il pulsante che smette
   * di rispondere.
   *
   * `rifiutato` — l'errore in cima alla pagina resta il messaggio che spiega
   * cosa fare; il pulsante dice solo, subito e dove sta l'occhio, che il
   * tentativo è finito male. Poi torna com'era, perché si riprova da lì.
   */
  const [esito, setEsito] = useState<'attesa' | 'confermato' | 'rifiutato'>('attesa');
  const attesa = useRef<number | null>(null);

  /* Il timer va spento se la pagina se ne va prima: `onSuccess` smonta questo
     componente, e un timer sopravvissuto chiamerebbe una funzione di qualcosa
     che non c'è più. */
  useEffect(
    () => () => {
      if (attesa.current !== null) window.clearTimeout(attesa.current);
    },
    [],
  );

  /** IN PROVA: variante dell'onda, da `?onda=`. Vedi il commento sull'SVG. */
  const [onda] = useState(() => {
    const richiesta = new URLSearchParams(window.location.search).get('onda');
    return richiesta !== null && richiesta in ONDE ? richiesta : '2-035';
  });

  const forma = ONDE[onda] ?? null;
  /* ~200 punti, calcolati a ogni render di una pagina che ne fa pochi. */
  const nastro = forma ? nastroOnda(forma.gobbe, forma.ampiezza, ONDA_SPESSORE) : null;
  // Sempre calcolato: e' il CSS a mostrarlo solo sul tema scuro, e costa
  // 97 punti — meno di un rerender in piu' per farlo comparire al cambio tema.
  const fondo = fondoOnda(forma?.gobbe ?? 2, forma?.ampiezza ?? 0);

  // Lo stato dell'SSO si chiede al server: mostrare un pulsante che porta a un
  // 404 è peggio che non mostrarlo.
  const sso = useQuery({
    queryKey: ['sso-status'],
    queryFn: () => api.ssoStatus(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  /**
   * Errore rimandato dal callback. Si legge una volta e si toglie dall'URL:
   * altrimenti resta appeso e ricompare a ogni ricarica.
   */
  const [ssoError] = useState(() => {
    const code = new URLSearchParams(window.location.search).get('sso_error');
    if (code) {
      window.history.replaceState(null, '', window.location.pathname);
      // Un codice non previsto non deve stampare sé stesso in pagina.
      return SSO_ERRORS[code] ?? SSO_ERRORS['errore']!;
    }
    return null;
  });

  /**
   * Tre click sul logo — oppure un errore SSO appena tornato, così chi ci è già
   * passato non deve ricliccare per riprovare.
   *
   * `rivelato` è separato da `ssoVisible` di proposito: se l'SSO non è
   * configurato il pulsante non si mostra (porterebbe a un 404), ma qualcosa
   * deve comunque comparire. Tre click che non producono nulla si leggono come
   * "è rotto", ed è esattamente la conclusione che ha tratto la prima persona
   * che ci ha provato.
   */
  const rivelato = logoClicks >= SSO_CLICKS || ssoError !== null;
  const ssoVisible = rivelato && sso.data?.enabled === true;
  const ssoNonConfigurato = rivelato && sso.isSuccess && sso.data.enabled === false;
  const ssoNonVerificabile = rivelato && sso.isError;

  const login = useMutation({
    mutationFn: () => api.login(username.trim(), password),
    onSuccess: () => {
      setPassword('');

      /* Chi ha chiesto meno movimento non deve aspettare un'animazione che non
         vedrà: per lui l'ingresso è immediato, come prima. */
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        onSuccess();
        return;
      }

      setEsito('confermato');
      /* 900ms: 450 di restringimento, 380 per il tratto della spunta, e il
         resto per vederla ferma un istante. Oltre il secondo l'attesa comincia
         a sembrare lentezza del sistema. */
      attesa.current = window.setTimeout(onSuccess, 900);
    },
    onError: () => {
      /* Il rifiuto non porta da nessuna parte: il pulsante torna com'era e
         resta il messaggio in cima. 1100ms — un po' più della conferma, perché
         qui non c'è una pagina nuova a dire che è successo qualcosa. */
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      setEsito('rifiutato');
      attesa.current = window.setTimeout(() => setEsito('attesa'), 1100);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    /* Un secondo tentativo non deve partire dal rosso del primo. */
    if (attesa.current !== null) window.clearTimeout(attesa.current);
    setEsito('attesa');
    login.mutate();
  };

  // L'errore SSO ha precedenza: è appena arrivato da un redirect, mentre quello
  // del form appartiene a un tentativo precedente.
  const message =
    ssoError ??
    (login.error instanceof ApiError
      ? login.error.message
      : login.error
        ? 'Accesso non riuscito. Riprova.'
        : null);

  return (
    <div className="login">
      {/* Fuori dalla shell non c'e' sidebar: il tema si cambia da qui. */}
      <ThemeToggle tone="bare" />

      {/*
        Il fondo scuro del tema scuro: la meta' di pagina a sinistra della
        curva. Sta qui, prima del pannello, perche' deve essere dipinto SOTTO
        il form; e non e' il fondo del pannello perche' la curva sconfina
        oltre il bordo del pannello e verrebbe tagliata di netto.
        Stesso viewBox del nastro (lato 1 = larghezza pagina), quindi le due
        curve condividono le coordinate e combaciano per costruzione.
      */}
      <svg
        className="login__fondo"
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        <path d={fondo} />
      </svg>

      <div className="login__panel" data-onda={onda}>
        <form className="login__card" onSubmit={submit} noValidate>
          <button
            type="button"
            className="login__logo-btn"
            // Non è un controllo che l'utente deve trovare: resta senza etichetta
            // visibile, ma con un nome accessibile che ne dice l'effetto.
            aria-label="Methodica"
            aria-pressed={ssoVisible}
            onClick={() => setLogoClicks((n) => n + 1)}
          >
            <img className="login__logo" src="/methodica-logo.png" alt="" />
          </button>
          <h1 className="login__title">my methodica</h1>
          <p className="login__subtitle">Portale documentale</p>

          {message ? (
            <div className="alert alert--error" role="alert">
              {message}
            </div>
          ) : null}

          <label className="login__field">
            <span>Utente</span>
            <input
              className="input"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              required
            />
          </label>

          <label className="login__field">
            <span>Password</span>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>

          <button
            className={`btn login__submit${
              esito === 'confermato' ? ' is-confermato' : esito === 'rifiutato' ? ' is-rifiutato' : ''
            }`}
            type="submit"
            disabled={esito !== 'attesa' || login.isPending || !username.trim() || !password}
          >
            {/* L'etichetta sbiadisce ma resta nel DOM: chi usa uno screen reader
                deve sentire com'è andata, non un pulsante diventato muto. */}
            <span className="login__submit-testo">
              {esito === 'confermato'
                ? 'Accesso riuscito'
                : esito === 'rifiutato'
                  ? 'Accesso rifiutato'
                  : login.isPending
                    ? 'Accesso in corso…'
                    : 'Accedi con Atlante'}
            </span>
            {/*
              I due segni restano sempre nel DOM e li accende il CSS. Se
              comparissero e sparissero con lo stato, al ritorno il segno
              svanirebbe di colpo mentre il cerchio si riapre — ed è quello che
              faceva sembrare il pulsante «un altro pulsante» invece dello
              stesso che torna.
            */}
            <Segno tipo="baffo" />
            <Segno tipo="croce" />
          </button>

          {rivelato ? (
            <>
              <div className="login__divider">
                <span>oppure</span>
              </div>

              {ssoVisible ? (
                <>
                  <a className="btn btn--ghost login__sso" href="/api/auth/sso/start">
                    <MicrosoftLogo />
                    Accedi con Microsoft
                  </a>
                  <p className="login__sso-note">
                    Riservato al personale interno Methodica.
                  </p>
                </>
              ) : (
                /* Il pulsante non c'è, ma la ragione sì: senza, i tre click
                   sembrano non aver funzionato. */
                <p className="login__sso-note login__sso-note--off">
                  <MicrosoftLogo />
                  {ssoNonVerificabile
                    ? 'Impossibile verificare l\'accesso Microsoft: servizio non raggiungibile.'
                    : ssoNonConfigurato
                      ? 'Accesso Microsoft non ancora configurato su questo ambiente.'
                      : 'Verifica dell\'accesso Microsoft in corso…'}
                </p>
              )}
            </>
          ) : null}

          <p className="login__help">
            Problemi di accesso? Contatta il tuo referente Methodica.
          </p>
        </form>
      </div>

      {/*
        Il confine fra le due metà.

        La pagina è tutta bianca: senza questo segno non ci sarebbe niente a dire
        dove finisce il form e comincia l'illustrazione. È l'unico elemento
        colorato della schermata, ed è il motivo per cui il logo torna leggibile
        — prima il fondo azzurro del pannello era lo stesso azzurro della M.

        Geometria: l'SVG copre tutta la griglia, quindi il suo `viewBox` di lato
        1 coincide con la larghezza della pagina e le coordinate del nastro si
        leggono come frazioni di essa. `preserveAspectRatio="none"` lo lascia
        stirare in altezza senza deformare la curva in larghezza.

        IN PROVA: la variante arriva da `?onda=` (2-035 è il default), vedi ONDE.
      */}
      {nastro ? (
        <svg
          className="login__onda"
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            {/*
              Dal mint del marchio in alto al blu profondo in basso. Il mint su
              bianco ha poco contrasto — è una scelta, non una svista: il nastro
              è spesso apposta, così in alto risulta un velo di colore invece di
              una riga sbiadita.

              Le estremità non hanno più bisogno di sfumare nel trasparente: ci
              pensa la forma, che finisce in punta prima del bordo. Una
              dissolvenza sopra una punta la farebbe solo sembrare sporca.

              `gradientUnits` di default è il riquadro della figura: il mint
              cade sulla punta in alto e il blu su quella in basso qualunque sia
              l'altezza della finestra.
            */}
            <linearGradient id="onda-tratto" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7be3bc" />
              <stop offset="48%" stopColor="#64a0c3" />
              <stop offset="100%" stopColor="#35709a" />
            </linearGradient>
          </defs>

          <path d={nastro} fill="url(#onda-tratto)" />
        </svg>
      ) : null}

      <aside className="login__aside">
        {/* --- IN PROVA: testo commentato per vedere il pannello con la sola
                illustrazione. Se resta così, va rimosso davvero insieme alle
                regole `.login__aside-content` in LoginPage.css.
        <div className="login__aside-content">
          <h2>I tuoi documenti contabili, in un posto solo.</h2>
          <ul>
            <li>Scarica registri, fatture ed estratti conto</li>
            <li>Carica i documenti da lavorare</li>
            <li>Accessibile direttamente da AtlantePlus</li>
          </ul>
        </div>
        --- */}

        {/*
          Illustrazione decorativa: `alt=""` e `aria-hidden`. Non descrive un
          contenuto — è un fregio — e un testo alternativo la farebbe annunciare
          da uno screen reader senza aggiungere informazione a una pagina che
          chiede solo utente e password.

          `loading="lazy"` no: sta nella prima schermata, e rinviarla
          produrrebbe un salto di layout appena caricata. `fetchPriority` bassa
          invece sì — il campo Utente deve essere utilizzabile prima che i
          126 KB del disegno siano arrivati.
        */}
        <img
          className="login__art"
          src="/login-scambio-documenti.originale.png"
          alt=""
          aria-hidden="true"
          width={832}
          height={1280}
          decoding="async"
          /*
           * Minuscolo e via spread: React 18 non mappa `fetchPriority` in
           * camelCase. L'attributo arriva comunque nel DOM, ma con un avviso
           * in console a ogni render — «React does not recognize the
           * fetchPriority prop». Così l'effetto è identico e la console resta
           * leggibile. In React 19 tornerà scrivibile in camelCase.
           */
          {...({ fetchpriority: 'low' } as React.ImgHTMLAttributes<HTMLImageElement>)}
        />
      </aside>
    </div>
  );
}

/**
 * Il segno dell'esito: spunta o croce.
 *
 * Il tratto si disegna da sé: `stroke-dasharray` lungo quanto il percorso e
 * `stroke-dashoffset` che va a zero — la linea non compare, viene tracciata.
 * Le lunghezze (26 per la spunta, 14 per ciascuna asta della croce) sono quelle
 * dei percorsi: più corte lasciano un pezzo già disegnato in partenza, più
 * lunghe ritardano l'inizio del tratto.
 *
 * La croce è in due percorsi e non in uno: le due aste si disegnano una dopo
 * l'altra, mentre un percorso unico traccerebbe anche il salto fra la fine
 * della prima e l'inizio della seconda.
 */
function Segno({ tipo }: { tipo: 'baffo' | 'croce' }) {
  return (
    <svg
      className={`login__segno login__segno--${tipo}`}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      {tipo === 'baffo' ? (
        <path className="login__segno-baffo" d="M5 12.5 10 17.5 19 7" />
      ) : (
        <>
          <path className="login__segno-asta" d="M7.5 7.5 16.5 16.5" />
          <path className="login__segno-asta login__segno-asta--due" d="M16.5 7.5 7.5 16.5" />
        </>
      )}
    </svg>
  );
}

/** I quattro quadrati Microsoft, nei colori ufficiali del marchio. */
function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="0" y="0" width="7" height="7" fill="#f25022" />
      <rect x="9" y="0" width="7" height="7" fill="#7fba00" />
      <rect x="0" y="9" width="7" height="7" fill="#00a4ef" />
      <rect x="9" y="9" width="7" height="7" fill="#ffb900" />
    </svg>
  );
}
